/**
 * Recorte de período da listagem de pedidos.
 *
 * O intervalo é resolvido AQUI, no navegador, e vai para a API como par de
 * instantes ISO. É o mesmo fuso em que a coluna "Data" da tabela é formatada —
 * se o backend recortasse "o dia 18" pelo relógio dele, o gestor veria pedidos
 * de fora do filtro (ou perderia os da virada) sem entender o motivo.
 */
export const periodosPedido = ['TODOS', 'HOJE', 'D7', 'D30', 'PERSONALIZADO'] as const;
export type PeriodoPedido = (typeof periodosPedido)[number];

export const ROTULO_PERIODO: Record<PeriodoPedido, string> = {
  TODOS: 'Todo o período',
  HOJE: 'Hoje',
  D7: 'Últimos 7 dias',
  D30: 'Últimos 30 dias',
  PERSONALIZADO: 'Personalizado',
};

/** Instantes ISO enviados à API (`undefined` = sem limite daquele lado). */
export interface IntervaloPedido {
  de?: string;
  ate?: string;
}

/** Dias do calendário escolhidos no modo personalizado (`yyyy-mm-dd`, ou vazio). */
export interface DiasPersonalizados {
  de: string;
  ate: string;
}

export function intervaloDoPeriodo(
  periodo: PeriodoPedido,
  personalizado: DiasPersonalizados,
  agora: Date = new Date(),
): IntervaloPedido {
  switch (periodo) {
    // Sempre no início do dia: o valor fica estável ao longo do dia inteiro e o
    // RTK Query não refaz a busca a cada re-render (a chave do cache é o ISO).
    case 'HOJE':
      return { de: inicioDoDia(agora).toISOString() };
    case 'D7':
      return { de: inicioDoDia(somarDias(agora, -6)).toISOString() };
    case 'D30':
      return { de: inicioDoDia(somarDias(agora, -29)).toISOString() };
    case 'PERSONALIZADO':
      return {
        de: instanteDoDia(personalizado.de, 'INICIO'),
        // O "até" é inclusive: quem escolhe 18 quer os pedidos do dia 18 todo.
        ate: instanteDoDia(personalizado.ate, 'FIM'),
      };
    default:
      return {};
  }
}

/** `yyyy-mm-dd` (input date) → instante ISO da borda do dia no fuso local. */
function instanteDoDia(dia: string, borda: 'INICIO' | 'FIM'): string | undefined {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia);
  if (!partes) return undefined;
  const ano = Number(partes[1]);
  const mes = Number(partes[2]);
  const data = Number(partes[3]);
  // Construído por partes de propósito: `new Date('2026-08-18')` é lido como
  // UTC pelo JS e adiantaria o recorte em 3h no Brasil.
  const inicio = borda === 'INICIO';
  return new Date(
    ano,
    mes - 1,
    data,
    inicio ? 0 : 23,
    inicio ? 0 : 59,
    inicio ? 0 : 59,
    inicio ? 0 : 999,
  ).toISOString();
}

function inicioDoDia(d: Date): Date {
  const zerado = new Date(d);
  zerado.setHours(0, 0, 0, 0);
  return zerado;
}

function somarDias(d: Date, dias: number): Date {
  const novo = new Date(d);
  novo.setDate(novo.getDate() + dias);
  return novo;
}
